import { useState, useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import { confirmAlert } from "react-confirm-alert";
import { useVideoTabs } from "./useVideoTabs";
import { ENDPOINTS } from "../utils/endpoints";

const emptySection = () => ({
  id: null,
  section_title: "",
  section_primary_text: "",
  section_secondary_text: "",
  banner_web_file: null,
  banner_web_url: null,
  delete_banner_web: false,
  banner_app_file: null,
  banner_app_url: null,
  delete_banner_app: false,
  image_web_file: null,
  image_web_url: null,
  delete_image_web: false,
  image_app_file: null,
  image_app_url: null,
  delete_image_app: false,
  video_tabs: [],
});

// Strip an absolute /uploads/ prefix that the client might echo back.
const cleanUploadPath = (url) => {
  if (!url) return null;
  if (url.includes("/uploads/")) {
    const parts = url.split("/uploads/");
    return parts[parts.length - 1];
  }
  return url;
};

// Map an API `sections` array into editor state (prefixes image URLs, resets
// file handles). Falls back to wrapping a legacy single-block `content` +
// `video_tabs` into one section when `sections` is absent.
export const sectionsFromApi = (data) => {
  const src =
    data?.sections && data.sections.length
      ? data.sections
      : data?.content
        ? [
            {
              id: data.content.id ?? null,
              section_title: data.content.content_title,
              section_primary_text: data.content.content_primary_text,
              section_secondary_text: data.content.content_secondary_text,
              banner_web: data.content.content_banner_web,
              banner_app: data.content.content_banner_app,
              image_web: data.content.content_image_web,
              image_app: data.content.content_image_app,
              video_tabs: data.content.video_tabs || data.content.video_links || [],
            },
          ]
        : [];

  const prefix = (p) => (p ? ENDPOINTS.IMAGE_BASE_URL + p : null);

  return src.map((s) => ({
    ...emptySection(),
    id: s.id ?? null,
    section_title: s.section_title || "",
    section_primary_text: s.section_primary_text || "",
    section_secondary_text: s.section_secondary_text || "",
    banner_web_url: prefix(s.banner_web),
    banner_app_url: prefix(s.banner_app),
    image_web_url: prefix(s.image_web),
    image_app_url: prefix(s.image_app),
    video_tabs: (s.video_tabs || []).map((tab) => ({
      ...tab,
      groups: (tab.groups || []).map((group) => ({
        ...group,
        group_banner_mobile_url: prefix(group.group_banner_mobile_url),
        group_banner_web_url: prefix(group.group_banner_web_url),
        videos: (group.videos || []).map((video) => ({
          ...video,
          cover_image: prefix(video.cover_image),
          attachment_file: null,
          attachment_file_url: prefix(video.attach_file),
          membership_expiry: video.membership_expiry || [],
        })),
      })),
    })),
  }));
};

// Build the `sections` JSON + all indexed file arrays for the multipart body.
// Returns { sections, section_files, group_files, video_attachments }.
export const buildSectionsPayload = (sections) => {
  const section_files = [];
  const group_files = [];
  const video_attachments = [];

  const findIndex = (arr, file) =>
    file
      ? arr.findIndex((f) => f?.name === file.name && f?.size === file.size)
      : -1;

  const sectionsJson = sections.map((section, sectionIdx) => {
    // Section images -> section_files with index references.
    const mapImage = (file, url, del, key) => {
      const res = {};
      if (file) {
        section_files.push(file);
        res[`${key}_index`] = section_files.length - 1;
      }
      res[key] = cleanUploadPath(url);
      if (del) res[`delete_${key}`] = true;
      return res;
    };

    const bannerWeb = mapImage(
      section.banner_web_file,
      section.banner_web_url,
      section.delete_banner_web,
      "banner_web",
    );
    const bannerApp = mapImage(
      section.banner_app_file,
      section.banner_app_url,
      section.delete_banner_app,
      "banner_app",
    );
    const imageWeb = mapImage(
      section.image_web_file,
      section.image_web_url,
      section.delete_image_web,
      "image_web",
    );
    const imageApp = mapImage(
      section.image_app_file,
      section.image_app_url,
      section.delete_image_app,
      "image_app",
    );

    const video_tabs = (section.video_tabs || []).map((tab, tabIdx) => ({
      ...(tab.id ? { id: tab.id } : {}),
      tab_name: tab.tab_name,
      sort_order: tabIdx,
      groups: (tab.groups || []).map((group, groupIdx) => {
        let group_banner_mobile_index = null;
        if (group.group_banner_mobile_file) {
          const i = findIndex(group_files, group.group_banner_mobile_file);
          if (i !== -1) group_banner_mobile_index = i;
          else {
            group_files.push(group.group_banner_mobile_file);
            group_banner_mobile_index = group_files.length - 1;
          }
        }
        let group_banner_web_index = null;
        if (group.group_banner_web_file) {
          const i = findIndex(group_files, group.group_banner_web_file);
          if (i !== -1) group_banner_web_index = i;
          else {
            group_files.push(group.group_banner_web_file);
            group_banner_web_index = group_files.length - 1;
          }
        }

        return {
          ...(group.id ? { id: group.id } : {}),
          group_title: group.group_title,
          group_description: group.group_description || null,
          sort_order: groupIdx,
          group_banner_mobile_url: cleanUploadPath(group.group_banner_mobile_url),
          group_banner_mobile_index,
          group_banner_web_url: cleanUploadPath(group.group_banner_web_url),
          group_banner_web_index,
          videos: (group.videos || []).map((video, videoIdx) => {
            let attach_file_index = null;
            if (video.attachment_file) {
              video_attachments.push(video.attachment_file);
              attach_file_index = video_attachments.length - 1;
            }
            return {
              id: video.id ?? null,
              title: video.title,
              url: video.url,
              video_type: video.video_type,
              membership_ids: video.membership_ids || [],
              sort_order: videoIdx,
              cover_image: cleanUploadPath(video.cover_image),
              cover_image_index: video.cover_image_index,
              membership_expiry: video.membership_expiry || [],
              attach_file: cleanUploadPath(video.attachment_file_url),
              attach_file_index,
              ...(video.delete_cover_image ? { delete_cover_image: true } : {}),
            };
          }),
        };
      }),
    }));

    return {
      ...(section.id ? { id: section.id } : {}),
      section_title: section.section_title || null,
      section_primary_text: section.section_primary_text || null,
      section_secondary_text: section.section_secondary_text || null,
      sort_order: sectionIdx,
      ...bannerWeb,
      ...bannerApp,
      ...imageWeb,
      ...imageApp,
      video_tabs,
    };
  });

  return { sections: sectionsJson, section_files, group_files, video_attachments };
};

export const usePageSections = () => {
  const [sections, setSections] = useState([]);
  const [activeSectionIndex, setActiveSectionIndex] = useState(0);
  // Cover files persist across sections for one submission.
  const [videoCoverFiles, setVideoCoverFiles] = useState([]);

  // Derive the active section's tab list + a setter that writes back into it.
  const activeVideoTabs = sections[activeSectionIndex]?.video_tabs || [];
  const setActiveVideoTabs = (updater) => {
    setSections((prev) =>
      prev.map((s, i) =>
        i === activeSectionIndex
          ? {
              ...s,
              video_tabs:
                typeof updater === "function"
                  ? updater(s.video_tabs || [])
                  : updater,
            }
          : s,
      ),
    );
  };

  const tabsState = useVideoTabs(activeSectionIndex, {
    videoTabs: activeVideoTabs,
    setVideoTabs: setActiveVideoTabs,
    videoCoverFiles,
    setVideoCoverFiles,
  });

  // Reset tab/group selection when the active section changes.
  useEffect(() => {
    tabsState.setActiveTabIndex(0);
    tabsState.setActiveGroupIndex(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSectionIndex]);

  const updateActiveSectionField = (field, value) => {
    setSections((prev) =>
      prev.map((s, i) =>
        i === activeSectionIndex ? { ...s, [field]: value } : s,
      ),
    );
  };

  const addSection = () => {
    setSections((prev) => [...prev, emptySection()]);
    setActiveSectionIndex(sections.length);
  };

  const deleteSection = (index) => {
    confirmAlert({
      title: "Confirm Delete",
      message:
        "Delete this section? All of its tabs, groups and videos will be removed.",
      buttons: [
        {
          label: "Yes",
          onClick: () => {
            setSections((prev) => prev.filter((_, i) => i !== index));
            setActiveSectionIndex((cur) =>
              cur >= sections.length - 1 ? Math.max(0, sections.length - 2) : cur,
            );
            toast.success("Section deleted");
          },
        },
        { label: "No" },
      ],
    });
  };

  const moveSectionUp = (index) => {
    if (index === 0) return;
    setSections((prev) => {
      const next = [...prev];
      [next[index - 1], next[index]] = [next[index], next[index - 1]];
      return next;
    });
    if (activeSectionIndex === index) setActiveSectionIndex(index - 1);
    else if (activeSectionIndex === index - 1) setActiveSectionIndex(index);
  };

  const moveSectionDown = (index) => {
    if (index >= sections.length - 1) return;
    setSections((prev) => {
      const next = [...prev];
      [next[index], next[index + 1]] = [next[index + 1], next[index]];
      return next;
    });
    if (activeSectionIndex === index) setActiveSectionIndex(index + 1);
    else if (activeSectionIndex === index + 1) setActiveSectionIndex(index);
  };

  const activeSection = useMemo(
    () => sections[activeSectionIndex],
    [sections, activeSectionIndex],
  );

  return {
    sections,
    setSections,
    activeSectionIndex,
    setActiveSectionIndex,
    activeSection,
    videoCoverFiles,
    setVideoCoverFiles,
    tabsState,
    updateActiveSectionField,
    addSection,
    deleteSection,
    moveSectionUp,
    moveSectionDown,
  };
};
